import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, FolderOpen, MapPin, Phone, Search } from 'lucide-react';
import api from '../api';

const usDate = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v || ''); };
const FORMS = [['SOC', 'soc-roc', 'Start of Care'], ['FU', 'recertification', 'Recert'], ['DC', 'discharge', 'Discharge']];

const Chip = ({ o }) => {
  if (!o) return <span className="text-gray-400">not started</span>;
  const tone = o.status === 'Locked' ? 'bg-green-100 text-green-800' : o.missing ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800';
  return <span className={`px-1.5 py-0.5 rounded-full ${tone}`}>{o.status === 'Locked' ? 'signed' : o.missing ? `${o.missing} left` : 'ready to sign'}</span>;
};

const PatientCard = ({ p }) => {
  const navigate = useNavigate();
  const openForm = (tp, path) => navigate(['Locked', 'Complete'].includes(p.oasis[tp]?.status) ? `/dashboard/forms/${path}?id=${p.oasis[tp].id}` : `/dashboard/forms/${path}?patientId=${p.patientId}`);
  return (
    <li className="border border-gray-200 rounded-lg p-3 hover:border-[#d4af37] transition-colors" data-testid={`caseload-card-${p.patientId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <button onClick={() => navigate(`/dashboard/patients/demographics/${p.patientId}`)} className="font-semibold text-gray-900 hover:underline" data-testid={`caseload-open-${p.patientId}`}>{p.name}</button>
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.via}</span>
      </div>
      <p className="text-xs text-gray-600 mt-1" data-testid={`caseload-ids-${p.patientId}`}>
        DOB {usDate(p.dateOfBirth) || '—'} · MRN {p.mrn || '—'} · Admission {usDate(p.admissionDate) || '—'}
      </p>
      <p className="text-xs text-gray-600">Dx: {p.diagnosis || '—'}</p>
      <div className="flex flex-wrap gap-x-4 mt-1 text-xs text-gray-600">
        {p.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#b8860b]"><MapPin className="w-3 h-3" />{p.address}</a>}
        {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:text-[#b8860b]"><Phone className="w-3 h-3" />{p.phone}</a>}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {FORMS.map(([tp, path, label]) => (
          <button key={tp} onClick={() => openForm(tp, path)} data-testid={`caseload-${tp.toLowerCase()}-${p.patientId}`}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 hover:bg-amber-50 hover:border-[#d4af37] flex items-center gap-1.5">
            <span className="font-semibold">{label}</span><Chip o={p.oasis[tp]} />
          </button>
        ))}
        <button onClick={() => navigate(`/dashboard/patients/demographics/${p.patientId}`)} data-testid={`caseload-chart-${p.patientId}`}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-900 text-[#d4af37] flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" /> Chart, notes and uploads</button>
      </div>
    </li>
  );
};

export const MyCaseloadBoard = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { api.get('/caseload/mine').then(r => setData(r.data)).catch(() => setData({ patients: [], isNurse: false })); }, []);
  if (!data || (!data.isNurse && !data.patients.length)) return null;
  const list = data.patients.filter(p => `${p.name} ${p.mrn}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="bg-white rounded border border-gray-200 shadow-sm" data-testid="my-caseload-board">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-gray-900 rounded-t">
        <h3 className="font-semibold text-[#d4af37] flex items-center gap-2"><ClipboardList className="w-4 h-4" /> My Caseload <span className="text-white/70 text-sm" data-testid="my-caseload-count">({data.patients.length} patients)</span></h3>
        {data.patients.length > 4 && (
          <label className="flex items-center gap-1 bg-white/10 rounded px-2" data-no-dictate><Search className="w-3.5 h-3.5 text-white/70" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find patient or MRN" className="bg-transparent text-sm text-white placeholder-white/50 py-1 outline-none w-44" data-testid="my-caseload-search" /></label>
        )}
      </div>
      {data.patients.length === 0
        ? <p className="p-4 text-sm text-gray-600" data-testid="my-caseload-empty">No patients are assigned to you yet. The office assigns patients from each patient's chart (Assign nurses).</p>
        : <ul className="p-3 grid gap-3 md:grid-cols-2" data-testid="my-caseload-list">{list.map(p => <PatientCard key={p.patientId} p={p} />)}{!list.length && <li className="text-sm text-gray-600" data-testid="my-caseload-no-match">No assigned patient matches "{q}".</li>}</ul>}
    </div>
  );
};
